import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import { isValidSvg } from "../../utils/svgUtils";
import {
    computeGridCubesAsync,
    connectionMarkerDefsSvg,
    drawablesNeedConnectionMarkers,
    estimateGridCubesObjectCount,
    estimateProjectionNdcBounds,
    formatGridCubesSegmentSvg,
    type ConnectionArrowMode,
    type ConnectionLineStyle,
    type ConnectionStyleMode,
    type GridCameraType,
    type GridNdcBounds,
    type GridConnectionMode,
    type GridPopulationMode,
    type GridCubesDrawable,
    type GridMaskBitmap,
    type GridCubesProgress,
} from "../../utils/gridCubesGeometry";
import { roundDeg } from "../../utils/primitivePointsPlacement";

const DEFAULT_PALETTE = ["#1f2933", "#3e4c59", "#7b8794", "#cbd2d9"];

interface UploadedSvg {
    name: string;
    content: string;
}

export interface GridMaskTransform {
    /** Uniform scale of the mask within the viewport (1 = fit). */
    scale: number;
    /** Horizontal position (%); 50 = centered. */
    offsetXPct: number;
    /** Vertical position (%); 50 = centered. */
    offsetYPct: number;
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
}

/** Rasterize an SVG into a coverage bitmap matching the viewport aspect. */
async function rasterizeMaskAsync(
    svgContent: string,
    aspect: number,
    transform: GridMaskTransform,
): Promise<GridMaskBitmap | null> {
    const maxDim = 256;
    const width =
        aspect >= 1 ? maxDim : Math.max(1, Math.round(maxDim * aspect));
    const height =
        aspect >= 1 ? Math.max(1, Math.round(maxDim / aspect)) : maxDim;
    const scale = Math.max(0.05, Math.min(8, transform.scale));
    const offsetX = (transform.offsetXPct / 100 - 0.5) * width;
    const offsetY = (transform.offsetYPct / 100 - 0.5) * height;
    const blob = new Blob([svgContent], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    try {
        const img = await loadImageElement(url);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return null;
        ctx.clearRect(0, 0, width, height);
        ctx.save();
        ctx.translate(width / 2 + offsetX, height / 2 + offsetY);
        ctx.scale(scale, scale);
        ctx.drawImage(img, -width / 2, -height / 2, width, height);
        ctx.restore();
        const data = ctx.getImageData(0, 0, width, height).data;
        const alpha = new Uint8ClampedArray(width * height);
        for (let i = 0; i < width * height; i++) {
            const j = i * 4;
            const a = data[j + 3]!;
            const luma = data[j]! + data[j + 1]! + data[j + 2]!;
            alpha[i] = a >= 128 || luma >= 64 ? 255 : 0;
        }
        return { width, height, alpha };
    } catch {
        return null;
    } finally {
        URL.revokeObjectURL(url);
    }
}

async function buildMaskBitmapsAsync(
    maskSvgs: UploadedSvg[],
    aspect: number,
    transform: GridMaskTransform,
): Promise<GridMaskBitmap[]> {
    if (maskSvgs.length === 0) return [];
    const bitmaps = await Promise.all(
        maskSvgs.map((svg) => rasterizeMaskAsync(svg.content, aspect, transform)),
    );
    return bitmaps.filter((m): m is GridMaskBitmap => m != null);
}

/** Semi-transparent PNG data URL for viewer overlay (union of masks). */
function maskPreviewDataUrlFromBitmaps(
    masks: GridMaskBitmap[],
    fillRgb = "224,122,95",
): string | null {
    if (masks.length === 0) return null;
    const width = masks[0]!.width;
    const height = masks[0]!.height;
    const union = new Uint8ClampedArray(width * height);
    for (const mask of masks) {
        if (mask.width !== width || mask.height !== height) continue;
        for (let i = 0; i < union.length; i++) {
            if (mask.alpha[i]! >= 128) union[i] = 255;
        }
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const imageData = ctx.createImageData(width, height);
    const [r, g, b] = fillRgb.split(",").map((n) => Number(n.trim()));
    for (let i = 0; i < union.length; i++) {
        const on = union[i]! > 0;
        const j = i * 4;
        imageData.data[j] = on ? r : 0;
        imageData.data[j + 1] = on ? g : 0;
        imageData.data[j + 2] = on ? b : 0;
        imageData.data[j + 3] = on ? 90 : 0;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL("image/png");
}

interface GridCubes3dState {
    gridX: number;
    gridY: number;
    gridZ: number;
    cellSize: number;
    density: number;
    populationMode: GridPopulationMode;
    maxObjectCells: number;
    seed: number;
    connectionsEnabled: boolean;
    connectionsClipToMask: boolean;
    connectionDensity: number;
    connectionMode: GridConnectionMode;
    connectionStyleMode: ConnectionStyleMode;
    connectionLineStyle: ConnectionLineStyle;
    connectionLineStylePathfinding: ConnectionLineStyle;
    connectionLineStyleDirect: ConnectionLineStyle;
    connectionStrandCount: number;
    connectionStrandSpacing: number;
    connectionCurved: boolean;
    connectionArrowMode: ConnectionArrowMode;
    segmentSubdivisions: number;
    connectionColor: string;
    connectionColorAlt: string;
    connectionWidth: number;
    connectionWidthAlt: number;
    rotationXDeg: number;
    rotationYDeg: number;
    rotationZDeg: number;
    cameraType: GridCameraType;
    cameraFovDeg: number;
    cameraDistance: number;
    viewBoxX: number;
    viewBoxY: number;
    viewBoxWidth: number;
    viewBoxHeight: number;
    backfaceCulling: boolean;
    palette: string[];
    ambient: number;
    maskEnabled: boolean;
    maskScale: number;
    maskOffsetXPct: number;
    maskOffsetYPct: number;
    maskPreviewEnabled: boolean;
    maskPreviewUrl: string | null;
    maskPreviewPending: boolean;
    projectionNdcBounds: GridNdcBounds | null;
    maskSvgs: UploadedSvg[];
    maskInputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
    faceStrokeEnabled: boolean;
    faceStrokeColor: string;
    faceStrokeWidth: number;
    error: string | null;
    drawables: GridCubesDrawable[];
    objectCount: number;
    faceCount: number;
    connectionCount: number;
    estimatedObjectCount: number;
    computePending: boolean;
    computeProgress: GridCubesProgress | null;
    setGridX: (v: number) => void;
    setGridY: (v: number) => void;
    setGridZ: (v: number) => void;
    setCellSize: (v: number) => void;
    setDensity: (v: number) => void;
    setPopulationMode: (v: GridPopulationMode) => void;
    setMaxObjectCells: (v: number) => void;
    setSeed: (v: number) => void;
    setConnectionsEnabled: (v: boolean) => void;
    setConnectionsClipToMask: (v: boolean) => void;
    setConnectionDensity: (v: number) => void;
    setConnectionMode: (v: GridConnectionMode) => void;
    setConnectionStyleMode: (v: ConnectionStyleMode) => void;
    setConnectionLineStyle: (v: ConnectionLineStyle) => void;
    setConnectionLineStylePathfinding: (v: ConnectionLineStyle) => void;
    setConnectionLineStyleDirect: (v: ConnectionLineStyle) => void;
    setConnectionStrandCount: (v: number) => void;
    setConnectionStrandSpacing: (v: number) => void;
    setConnectionCurved: (v: boolean) => void;
    setConnectionArrowMode: (v: ConnectionArrowMode) => void;
    setSegmentSubdivisions: (v: number) => void;
    setConnectionColor: (v: string) => void;
    setConnectionColorAlt: (v: string) => void;
    setConnectionWidth: (v: number) => void;
    setConnectionWidthAlt: (v: number) => void;
    setRotationXDeg: (v: number) => void;
    setRotationYDeg: (v: number) => void;
    setRotationZDeg: (v: number) => void;
    setCameraType: (v: GridCameraType) => void;
    setCameraFovDeg: (v: number) => void;
    setCameraDistance: (v: number) => void;
    setViewBoxX: (v: number) => void;
    setViewBoxY: (v: number) => void;
    setViewBoxWidth: (v: number) => void;
    setViewBoxHeight: (v: number) => void;
    setBackfaceCulling: (v: boolean) => void;
    setPalette: (v: string[]) => void;
    setAmbient: (v: number) => void;
    setMaskEnabled: (v: boolean) => void;
    setMaskScale: (v: number) => void;
    setMaskOffsetXPct: (v: number) => void;
    setMaskOffsetYPct: (v: number) => void;
    setMaskPreviewEnabled: (v: boolean) => void;
    addMaskSvg: (svg: UploadedSvg) => void;
    removeMaskSvg: (index: number) => void;
    handleMaskFileLoad: (
        index?: number,
    ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
    setFaceStrokeEnabled: (v: boolean) => void;
    setFaceStrokeColor: (v: string) => void;
    setFaceStrokeWidth: (v: number) => void;
    setError: (v: string | null) => void;
    generate: () => Promise<void>;
    cancelCompute: () => void;
    viewBoxAttr: string;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const GridCubes3dContext = createContext<GridCubes3dState | null>(null);

export function useGridCubes3d() {
    const ctx = useContext(GridCubes3dContext);
    if (!ctx) {
        throw new Error(
            "useGridCubes3d must be used within GridCubes3dProvider",
        );
    }
    return ctx;
}

function round2(n: number): number {
    return Math.round(n * 100) / 100;
}

export function GridCubes3dProvider({ children }: { children: React.ReactNode }) {
    const [gridX, setGridX] = useState(6);
    const [gridY, setGridY] = useState(6);
    const [gridZ, setGridZ] = useState(6);
    const [cellSize, setCellSize] = useState(1);
    const [density, setDensity] = useState(0.35);
    const [populationMode, setPopulationMode] =
        useState<GridPopulationMode>("uniform");
    const [maxObjectCells, setMaxObjectCells] = useState(2);
    const [seed, setSeed] = useState(1);
    const [connectionsEnabled, setConnectionsEnabled] = useState(true);
    const [connectionsClipToMask, setConnectionsClipToMask] = useState(true);
    const [connectionDensity, setConnectionDensity] = useState(0.6);
    const [connectionMode, setConnectionMode] =
        useState<GridConnectionMode>("pathfinding");
    const [connectionStyleMode, setConnectionStyleMode] =
        useState<ConnectionStyleMode>("uniform");
    const [connectionLineStyle, setConnectionLineStyle] =
        useState<ConnectionLineStyle>("solid");
    const [connectionLineStylePathfinding, setConnectionLineStylePathfinding] =
        useState<ConnectionLineStyle>("dashed");
    const [connectionLineStyleDirect, setConnectionLineStyleDirect] =
        useState<ConnectionLineStyle>("solid");
    const [connectionStrandCount, setConnectionStrandCount] = useState(1);
    const [connectionStrandSpacing, setConnectionStrandSpacing] = useState(3);
    const [connectionCurved, setConnectionCurved] = useState(false);
    const [connectionArrowMode, setConnectionArrowMode] =
        useState<ConnectionArrowMode>("none");
    const [segmentSubdivisions, setSegmentSubdivisions] = useState(6);
    const [connectionColor, setConnectionColor] = useState("#e07a5f");
    const [connectionColorAlt, setConnectionColorAlt] = useState("#81b29a");
    const [connectionWidth, setConnectionWidth] = useState(1.5);
    const [connectionWidthAlt, setConnectionWidthAlt] = useState(1);
    const [rotationXDeg, setRotationXDeg] = useState(-20);
    const [rotationYDeg, setRotationYDeg] = useState(35);
    const [rotationZDeg, setRotationZDeg] = useState(0);
    const [cameraType, setCameraType] =
        useState<GridCameraType>("perspective");
    const [cameraFovDeg, setCameraFovDeg] = useState(45);
    const [cameraDistance, setCameraDistance] = useState(12);
    const [viewBoxX, setViewBoxX] = useState(0);
    const [viewBoxY, setViewBoxY] = useState(0);
    const [viewBoxWidth, setViewBoxWidth] = useState(600);
    const [viewBoxHeight, setViewBoxHeight] = useState(600);
    const [backfaceCulling, setBackfaceCulling] = useState(true);
    const [palette, setPalette] = useState<string[]>(DEFAULT_PALETTE);
    const [ambient, setAmbient] = useState(0.25);
    const [maskEnabled, setMaskEnabled] = useState(false);
    const [maskScale, setMaskScale] = useState(1);
    const [maskOffsetXPct, setMaskOffsetXPct] = useState(50);
    const [maskOffsetYPct, setMaskOffsetYPct] = useState(50);
    const [maskPreviewEnabled, setMaskPreviewEnabled] = useState(false);
    const [maskPreviewUrl, setMaskPreviewUrl] = useState<string | null>(null);
    const [maskPreviewPending, setMaskPreviewPending] = useState(false);
    const [projectionNdcBounds, setProjectionNdcBounds] =
        useState<GridNdcBounds | null>(null);
    const [maskSvgs, setMaskSvgs] = useState<UploadedSvg[]>([]);
    const [faceStrokeEnabled, setFaceStrokeEnabled] = useState(true);
    const [faceStrokeColor, setFaceStrokeColor] = useState("#11161c");
    const [faceStrokeWidth, setFaceStrokeWidth] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const [drawables, setDrawables] = useState<GridCubesDrawable[]>([]);
    const [objectCount, setObjectCount] = useState(0);
    const [faceCount, setFaceCount] = useState(0);
    const [connectionCount, setConnectionCount] = useState(0);
    const [computePending, setComputePending] = useState(false);
    const [computeProgress, setComputeProgress] =
        useState<GridCubesProgress | null>(null);

    const maskInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const computeAbortRef = useRef<AbortController | null>(null);
    const lastRotationRef = useRef({
        x: rotationXDeg,
        y: rotationYDeg,
        z: rotationZDeg,
    });
    const hasGeneratedRef = useRef(false);

    const viewBoxAttr = `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`;

    const maskTransform = useMemo(
        (): GridMaskTransform => ({
            scale: maskScale,
            offsetXPct: maskOffsetXPct,
            offsetYPct: maskOffsetYPct,
        }),
        [maskScale, maskOffsetXPct, maskOffsetYPct],
    );

    const maskOverlayNdcBounds = useMemo(() => {
        if (projectionNdcBounds) return projectionNdcBounds;
        return estimateProjectionNdcBounds({
            gridX,
            gridY,
            gridZ,
            cellSize,
            rotationXDeg,
            rotationYDeg,
            rotationZDeg,
            cameraType,
            cameraFovDeg,
            cameraDistance,
            outputWidth: viewBoxWidth,
            outputHeight: viewBoxHeight,
        });
    }, [
        projectionNdcBounds,
        gridX,
        gridY,
        gridZ,
        cellSize,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraType,
        cameraFovDeg,
        cameraDistance,
        viewBoxWidth,
        viewBoxHeight,
    ]);

    const estimatedObjectCount = useMemo(
        () =>
            estimateGridCubesObjectCount(
                gridX,
                gridY,
                gridZ,
                density,
                maxObjectCells,
            ),
        [gridX, gridY, gridZ, density, maxObjectCells],
    );

    useEffect(() => {
        if (!maskPreviewEnabled || maskSvgs.length === 0) {
            setMaskPreviewUrl(null);
            setMaskPreviewPending(false);
            return;
        }
        let cancelled = false;
        setMaskPreviewPending(true);
        const aspect = viewBoxWidth / Math.max(1, viewBoxHeight);
        void buildMaskBitmapsAsync(maskSvgs, aspect, maskTransform).then(
            (bitmaps) => {
                if (cancelled) return;
                setMaskPreviewUrl(maskPreviewDataUrlFromBitmaps(bitmaps));
                setMaskPreviewPending(false);
            },
        );
        return () => {
            cancelled = true;
        };
    }, [
        maskPreviewEnabled,
        maskSvgs,
        maskTransform,
        viewBoxWidth,
        viewBoxHeight,
    ]);

    const addMaskSvg = useCallback((svg: UploadedSvg) => {
        setMaskSvgs((prev) => [...prev, svg]);
        setMaskEnabled(true);
    }, []);

    const removeMaskSvg = useCallback((index: number) => {
        setMaskSvgs((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleMaskFileLoad =
        (index?: number) =>
        (event: React.ChangeEvent<HTMLInputElement>): void => {
            const file = event.target.files?.[0];
            if (!file) return;
            setError(null);
            if (
                !file.type.includes("svg") &&
                !file.name.toLowerCase().endsWith(".svg")
            ) {
                setError("Please choose an SVG file for the mask.");
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const text =
                    typeof reader.result === "string" ? reader.result : "";
                if (!text || !isValidSvg(text)) {
                    setError("Selected file does not appear to be valid SVG.");
                    return;
                }
                const payload: UploadedSvg = { name: file.name, content: text };
                if (index == null) {
                    addMaskSvg(payload);
                } else {
                    setMaskSvgs((prev) => {
                        const next = [...prev];
                        next[index] = payload;
                        return next;
                    });
                }
            };
            reader.onerror = () => setError("Error reading mask file.");
            reader.readAsText(file);
        };

    const generate = useCallback(async () => {
        setError(null);
        computeAbortRef.current?.abort();
        const ac = new AbortController();
        computeAbortRef.current = ac;
        setComputePending(true);
        setComputeProgress({ phase: "populate", processed: 0, total: 1 });
        try {
            const aspect = viewBoxWidth / Math.max(1, viewBoxHeight);
            const masks: GridMaskBitmap[] =
                maskEnabled && maskSvgs.length > 0
                    ? await buildMaskBitmapsAsync(maskSvgs, aspect, maskTransform)
                    : [];
            const result = await computeGridCubesAsync({
                gridX,
                gridY,
                gridZ,
                cellSize,
                density,
                populationMode,
                maxObjectCells,
                seed,
                connectionsEnabled,
                connectionsClipToMask,
                connectionDensity,
                connectionMode,
                connectionStyleMode,
                connectionLineStyle,
                connectionLineStylePathfinding,
                connectionLineStyleDirect,
                connectionStrandCount,
                connectionStrandSpacing,
                connectionCurved,
                connectionArrowMode,
                connectionColor,
                connectionColorAlt,
                connectionWidth,
                connectionWidthAlt,
                segmentSubdivisions,
                rotationXDeg,
                rotationYDeg,
                rotationZDeg,
                cameraType,
                cameraFovDeg,
                cameraDistance,
                backfaceCulling,
                palette,
                ambient,
                maskEnabled,
                masks,
                outputMinX: viewBoxX,
                outputMinY: viewBoxY,
                outputWidth: viewBoxWidth,
                outputHeight: viewBoxHeight,
                signal: ac.signal,
                onProgress: (info) => setComputeProgress(info),
            });
            setDrawables(result.drawables);
            setObjectCount(result.objectCount);
            setFaceCount(result.faceCount);
            setConnectionCount(result.connectionCount);
            setProjectionNdcBounds(result.ndcBounds);
            hasGeneratedRef.current = true;
        } catch (e) {
            if (!(e instanceof DOMException && e.name === "AbortError")) {
                console.warn("Grid cubes compute failed:", e);
                setError("Could not generate the grid.");
                setDrawables([]);
            }
        } finally {
            if (computeAbortRef.current === ac) {
                computeAbortRef.current = null;
            }
            setComputePending(false);
            setComputeProgress(null);
        }
    }, [
        gridX,
        gridY,
        gridZ,
        cellSize,
        density,
        populationMode,
        maxObjectCells,
        seed,
        connectionsEnabled,
        connectionsClipToMask,
        connectionDensity,
        connectionMode,
        connectionStyleMode,
        connectionLineStyle,
        connectionLineStylePathfinding,
        connectionLineStyleDirect,
        connectionStrandCount,
        connectionStrandSpacing,
        connectionCurved,
        connectionArrowMode,
        connectionColor,
        connectionColorAlt,
        connectionWidth,
        connectionWidthAlt,
        segmentSubdivisions,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraType,
        cameraFovDeg,
        cameraDistance,
        backfaceCulling,
        palette,
        ambient,
        maskEnabled,
        maskTransform,
        maskSvgs,
        viewBoxX,
        viewBoxY,
        viewBoxWidth,
        viewBoxHeight,
    ]);

    const cancelCompute = useCallback(() => {
        computeAbortRef.current?.abort();
        computeAbortRef.current = null;
        setComputePending(false);
        setComputeProgress(null);
    }, []);

    useEffect(() => {
        const prev = lastRotationRef.current;
        const didRotationChange =
            prev.x !== rotationXDeg ||
            prev.y !== rotationYDeg ||
            prev.z !== rotationZDeg;
        lastRotationRef.current = {
            x: rotationXDeg,
            y: rotationYDeg,
            z: rotationZDeg,
        };
        if (!didRotationChange) return;
        if (!hasGeneratedRef.current) return;
        const timeoutId = setTimeout(() => {
            void generate();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [rotationXDeg, rotationYDeg, rotationZDeg, generate]);

    const canExport = drawables.length > 0;

    const buildExportSvgString = useCallback(() => {
        if (!canExport) return "";
        const body = drawables
            .map((d) => {
                if (d.kind === "face") {
                    const pts = d.points
                        .map((p) => `${round2(p.x)},${round2(p.y)}`)
                        .join(" ");
                    const stroke = faceStrokeEnabled
                        ? ` stroke="${faceStrokeColor}" stroke-width="${faceStrokeWidth}" stroke-linejoin="round"`
                        : "";
                    return `<polygon points="${pts}" fill="${d.color}"${stroke} />`;
                }
                return formatGridCubesSegmentSvg(d, round2);
            })
            .join("\n");
        const defs = drawablesNeedConnectionMarkers(drawables)
            ? `\n  ${connectionMarkerDefsSvg()}\n`
            : "";
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">${defs}
  <g class="grid-cubes">
${body}
  </g>
</svg>`;
    }, [canExport, drawables, faceStrokeEnabled, faceStrokeColor, faceStrokeWidth, viewBoxAttr]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "grid-cubes-3d.svg";
        a.click();
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, canExport]);


    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            gridX,
            gridY,
            gridZ,
            cellSize,
            density,
            populationMode,
            maxObjectCells,
            seed,
            connectionsEnabled,
            connectionsClipToMask,
            connectionDensity,
            connectionMode,
            connectionStyleMode,
            connectionLineStyle,
            connectionLineStylePathfinding,
            connectionLineStyleDirect,
            connectionStrandCount,
            connectionStrandSpacing,
            connectionCurved,
            connectionArrowMode,
            segmentSubdivisions,
            connectionColor,
            connectionColorAlt,
            connectionWidth,
            connectionWidthAlt,
            rotationXDeg,
            rotationYDeg,
            rotationZDeg,
            cameraType,
            cameraFovDeg,
            cameraDistance,
            viewBoxX,
            viewBoxY,
            viewBoxWidth,
            viewBoxHeight,
            backfaceCulling,
            palette,
            ambient,
            maskEnabled,
            maskScale,
            maskOffsetXPct,
            maskOffsetYPct,
            maskPreviewEnabled,
            faceStrokeEnabled,
            faceStrokeColor,
            faceStrokeWidth,
        }),
        [
            gridX,
            gridY,
            gridZ,
            cellSize,
            density,
            populationMode,
            maxObjectCells,
            seed,
            connectionsEnabled,
            connectionsClipToMask,
            connectionDensity,
            connectionMode,
            connectionStyleMode,
            connectionLineStyle,
            connectionLineStylePathfinding,
            connectionLineStyleDirect,
            connectionStrandCount,
            connectionStrandSpacing,
            connectionCurved,
            connectionArrowMode,
            segmentSubdivisions,
            connectionColor,
            connectionColorAlt,
            connectionWidth,
            connectionWidthAlt,
            rotationXDeg,
            rotationYDeg,
            rotationZDeg,
            cameraType,
            cameraFovDeg,
            cameraDistance,
            viewBoxX,
            viewBoxY,
            viewBoxWidth,
            viewBoxHeight,
            backfaceCulling,
            palette,
            ambient,
            maskEnabled,
            maskScale,
            maskOffsetXPct,
            maskOffsetYPct,
            maskPreviewEnabled,
            faceStrokeEnabled,
            faceStrokeColor,
            faceStrokeWidth,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.gridX != null) setGridX(values.gridX as number);
        if (values.gridY != null) setGridY(values.gridY as number);
        if (values.gridZ != null) setGridZ(values.gridZ as number);
        if (values.cellSize != null) setCellSize(values.cellSize as number);
        if (values.density != null) setDensity(values.density as number);
        const popMode = (v: unknown): v is GridPopulationMode =>
            v === "uniform" ||
            v === "center" ||
            v === "edges" ||
            v === "bottom" ||
            v === "top";
        if (popMode(values.populationMode)) {
            setPopulationMode(values.populationMode);
        }
        if (values.maxObjectCells != null) {
            setMaxObjectCells(values.maxObjectCells as number);
        }
        if (values.seed != null) setSeed(values.seed as number);
        if (values.connectionsEnabled != null) {
            setConnectionsEnabled(Boolean(values.connectionsEnabled));
        }
        if (values.connectionsClipToMask != null) {
            setConnectionsClipToMask(Boolean(values.connectionsClipToMask));
        }
        if (values.connectionDensity != null) {
            setConnectionDensity(values.connectionDensity as number);
        }
        if (
            values.connectionMode === "pathfinding" ||
            values.connectionMode === "direct"
        ) {
            setConnectionMode(values.connectionMode);
        }
        if (values.segmentSubdivisions != null) {
            setSegmentSubdivisions(values.segmentSubdivisions as number);
        }
        if (values.connectionColor != null) {
            setConnectionColor(values.connectionColor as string);
        }
        if (values.connectionWidth != null) {
            setConnectionWidth(values.connectionWidth as number);
        }
        if (values.connectionColorAlt != null) {
            setConnectionColorAlt(values.connectionColorAlt as string);
        }
        if (values.connectionWidthAlt != null) {
            setConnectionWidthAlt(values.connectionWidthAlt as number);
        }
        if (
            values.connectionStyleMode === "uniform" ||
            values.connectionStyleMode === "random" ||
            values.connectionStyleMode === "byRouting"
        ) {
            setConnectionStyleMode(values.connectionStyleMode);
        }
        const lineStyle = (v: unknown): v is ConnectionLineStyle =>
            v === "solid" || v === "dashed" || v === "dotted";
        if (lineStyle(values.connectionLineStyle)) {
            setConnectionLineStyle(values.connectionLineStyle);
        }
        if (lineStyle(values.connectionLineStylePathfinding)) {
            setConnectionLineStylePathfinding(
                values.connectionLineStylePathfinding,
            );
        }
        if (lineStyle(values.connectionLineStyleDirect)) {
            setConnectionLineStyleDirect(values.connectionLineStyleDirect);
        }
        if (values.connectionStrandCount != null) {
            setConnectionStrandCount(values.connectionStrandCount as number);
        }
        if (values.connectionStrandSpacing != null) {
            setConnectionStrandSpacing(
                values.connectionStrandSpacing as number,
            );
        }
        if (values.connectionCurved != null) {
            setConnectionCurved(Boolean(values.connectionCurved));
        }
        const arrowMode = (v: unknown): v is ConnectionArrowMode =>
            v === "none" ||
            v === "end" ||
            v === "both" ||
            v === "random";
        if (arrowMode(values.connectionArrowMode)) {
            setConnectionArrowMode(values.connectionArrowMode);
        }
        if (values.rotationXDeg != null) {
            setRotationXDeg(roundDeg(values.rotationXDeg as number));
        }
        if (values.rotationYDeg != null) {
            setRotationYDeg(roundDeg(values.rotationYDeg as number));
        }
        if (values.rotationZDeg != null) {
            setRotationZDeg(roundDeg(values.rotationZDeg as number));
        }
        if (
            values.cameraType === "perspective" ||
            values.cameraType === "orthographic"
        ) {
            setCameraType(values.cameraType);
        }
        if (values.cameraFovDeg != null) {
            setCameraFovDeg(values.cameraFovDeg as number);
        }
        if (values.cameraDistance != null) {
            setCameraDistance(values.cameraDistance as number);
        }
        if (values.viewBoxX != null) setViewBoxX(values.viewBoxX as number);
        if (values.viewBoxY != null) setViewBoxY(values.viewBoxY as number);
        if (values.viewBoxWidth != null) {
            setViewBoxWidth(values.viewBoxWidth as number);
        }
        if (values.viewBoxHeight != null) {
            setViewBoxHeight(values.viewBoxHeight as number);
        }
        if (values.backfaceCulling != null) {
            setBackfaceCulling(Boolean(values.backfaceCulling));
        }
        if (Array.isArray(values.palette)) {
            setPalette(values.palette as string[]);
        }
        if (values.ambient != null) setAmbient(values.ambient as number);
        if (values.maskEnabled != null) {
            setMaskEnabled(Boolean(values.maskEnabled));
        }
        if (values.maskPreviewEnabled != null) {
            setMaskPreviewEnabled(Boolean(values.maskPreviewEnabled));
        }
        if (values.maskScale != null) setMaskScale(values.maskScale as number);
        if (values.maskOffsetXPct != null) {
            setMaskOffsetXPct(values.maskOffsetXPct as number);
        }
        if (values.maskOffsetYPct != null) {
            setMaskOffsetYPct(values.maskOffsetYPct as number);
        }
        if (values.faceStrokeEnabled != null) {
            setFaceStrokeEnabled(Boolean(values.faceStrokeEnabled));
        }
        if (values.faceStrokeColor != null) {
            setFaceStrokeColor(values.faceStrokeColor as string);
        }
        if (values.faceStrokeWidth != null) {
            setFaceStrokeWidth(values.faceStrokeWidth as number);
        }
        hasGeneratedRef.current = false;
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "grid-cubes-3d",
        defaultPreset: {
            gridX: 6,
            gridY: 6,
            gridZ: 6,
            cellSize: 1,
            density: 0.35,
            populationMode: "uniform",
            maxObjectCells: 2,
            seed: 1,
            connectionsEnabled: true,
            connectionsClipToMask: true,
            connectionDensity: 0.6,
            connectionMode: "pathfinding",
            connectionStyleMode: "uniform",
            connectionLineStyle: "solid",
            connectionLineStylePathfinding: "dashed",
            connectionLineStyleDirect: "solid",
            connectionStrandCount: 1,
            connectionStrandSpacing: 3,
            connectionCurved: false,
            connectionArrowMode: "none",
            segmentSubdivisions: 6,
            connectionColor: "#e07a5f",
            connectionColorAlt: "#81b29a",
            connectionWidth: 1.5,
            connectionWidthAlt: 1,
            rotationXDeg: -20,
            rotationYDeg: 35,
            rotationZDeg: 0,
            cameraType: "perspective",
            cameraFovDeg: 45,
            cameraDistance: 12,
            viewBoxX: 0,
            viewBoxY: 0,
            viewBoxWidth: 600,
            viewBoxHeight: 600,
            backfaceCulling: true,
            palette: DEFAULT_PALETTE,
            ambient: 0.25,
            maskEnabled: false,
            maskScale: 1,
            maskOffsetXPct: 50,
            maskOffsetYPct: 50,
            maskPreviewEnabled: false,
            faceStrokeEnabled: true,
            faceStrokeColor: "#11161c",
            faceStrokeWidth: 1,
        },
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "grid-cubes-3d",
        {
            onObject: (svg) => {
                setError(null);
                addMaskSvg(svg);
            },
            onPrimary: (svg) => {
                setError(null);
                addMaskSvg(svg);
            },
        },
        { setError },
    );

    const value: GridCubes3dState = {
        gridX,
        gridY,
        gridZ,
        cellSize,
        density,
        populationMode,
        maxObjectCells,
        seed,
        connectionsEnabled,
        connectionsClipToMask,
        connectionDensity,
        connectionMode,
        connectionStyleMode,
        connectionLineStyle,
        connectionLineStylePathfinding,
        connectionLineStyleDirect,
        connectionStrandCount,
        connectionStrandSpacing,
        connectionCurved,
        connectionArrowMode,
        segmentSubdivisions,
        connectionColor,
        connectionColorAlt,
        connectionWidth,
        connectionWidthAlt,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraType,
        cameraFovDeg,
        cameraDistance,
        viewBoxX,
        viewBoxY,
        viewBoxWidth,
        viewBoxHeight,
        backfaceCulling,
        palette,
        ambient,
        maskEnabled,
        maskScale,
        maskOffsetXPct,
        maskOffsetYPct,
        maskPreviewEnabled,
        maskPreviewUrl,
        maskPreviewPending,
        projectionNdcBounds: maskOverlayNdcBounds,
        maskSvgs,
        maskInputRefs,
        faceStrokeEnabled,
        faceStrokeColor,
        faceStrokeWidth,
        error,
        drawables,
        objectCount,
        faceCount,
        connectionCount,
        estimatedObjectCount,
        computePending,
        computeProgress,
        setGridX,
        setGridY,
        setGridZ,
        setCellSize,
        setDensity,
        setPopulationMode,
        setMaxObjectCells,
        setSeed,
        setConnectionsEnabled,
        setConnectionsClipToMask,
        setConnectionDensity,
        setConnectionMode,
        setConnectionStyleMode,
        setConnectionLineStyle,
        setConnectionLineStylePathfinding,
        setConnectionLineStyleDirect,
        setConnectionStrandCount,
        setConnectionStrandSpacing,
        setConnectionCurved,
        setConnectionArrowMode,
        setSegmentSubdivisions,
        setConnectionColor,
        setConnectionColorAlt,
        setConnectionWidth,
        setConnectionWidthAlt,
        setRotationXDeg,
        setRotationYDeg,
        setRotationZDeg,
        setCameraType,
        setCameraFovDeg,
        setCameraDistance,
        setViewBoxX,
        setViewBoxY,
        setViewBoxWidth,
        setViewBoxHeight,
        setBackfaceCulling,
        setPalette,
        setAmbient,
        setMaskEnabled,
        setMaskScale,
        setMaskOffsetXPct,
        setMaskOffsetYPct,
        setMaskPreviewEnabled,
        addMaskSvg,
        removeMaskSvg,
        handleMaskFileLoad,
        setFaceStrokeEnabled,
        setFaceStrokeColor,
        setFaceStrokeWidth,
        setError,
        generate,
        cancelCompute,
        viewBoxAttr,
        canExport,
        buildExportSvgString,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <GridCubes3dContext.Provider value={value}>
            {children}
        </GridCubes3dContext.Provider>
    );
}
